import type { NewInsuredFormValues } from '../types/vida/cadastralMaintenance/newInsuredFormTypes';
import type {
  AddConjugeSeguradoDataRequest,
  AddConjugeSeguradoDataResponse,
  AddSeguradoDataRequest,
  AddSeguradoDataResponse,
  ConsultarSeguradosFaturamentoItem,
  GetConsultarSeguradosFaturamentoParams,
  GetSeguradoListParams,
  RemoveSeguradoDataRequest,
  SeguradoBeneficiaryInclusionItem,
  SeguradoDataReturn,
  SeguradoBeneficiaryUpdateItem,
  SeguradoDetailBeneficiaryItem,
  SeguradoDetailItem,
  SeguradoListItem,
  UpdateSeguradoDataRequest,
  UpdateSeguradoDataResponse,
} from '../types/vida/maintenanceSegurado/maintenanceSeguradoType';
import type { BeneficiarioTableItem } from '../types/vida/table/beneficiariosTableType';
import type { SelfBillingTableItem } from '../types/vida/table/selfBillingTableType';
import {
  formatDateForDateInput,
  mapSexLabelToCode,
  resolveCodigoUsuarioInclusao,
} from './inclusionProponentUtil';
import { formatCepForInput, getCepDigits } from './cepFieldUtils';
import {
  formatLogradouroWithNumeroForApi,
  formatSalaryDecimalForApi,
  formatSalaryForFormInitial,
  parseFormMoneyToNumber,
  parseLogradouroFromApi,
} from './insuredFormUtils';
import { resolveSeguradoParentescoLabel } from './parentescoUtils';

export const SEGURADOS_SORT_OPTIONS = [
  { label: 'Matricula (A-Z)', value: 'matricula' },
  { label: 'Matricula (Z-A)', value: 'matricula-desc' },
  { label: 'Tipo Segurado (A-Z)', value: 'tipoSegurado' },
  { label: 'Tipo Segurado (Z-A)', value: 'tipoSegurado-desc' },
  { label: 'Nome (A-Z)', value: 'nome' },
  { label: 'Nome (Z-A)', value: 'nome-desc' },
  { label: 'CPF (A-Z)', value: 'cpf' },
  { label: 'CPF (Z-A)', value: 'cpf-desc' },
  { label: 'Status (A-Z)', value: 'status' },
  { label: 'Status (Z-A)', value: 'status-desc' },
  { label: 'Certificado (A-Z)', value: 'certificado' },
  { label: 'Certificado (Z-A)', value: 'certificado-desc' },
];

export const isSelfBillingApiErrorMessage = (mensagem?: string | null): boolean =>
  mensagem?.toLowerCase().includes('erro') ?? false;

export class SeguradoInconsistencyError extends Error {
  readonly inconsistencies: SeguradoDataReturn[];
  readonly status = 400;

  constructor(inconsistencies: SeguradoDataReturn[], cause?: unknown) {
    super('Inconsistências encontradas ao incluir segurado');
    this.name = 'SeguradoInconsistencyError';
    this.inconsistencies = inconsistencies;

    if (cause instanceof Error) {
      this.cause = cause;
    }
  }
}

export const normalizeSeguradoInconsistencies = (
  retorno: SeguradoDataReturn[] | null | undefined,
): SeguradoDataReturn[] => {
  if (!Array.isArray(retorno) || retorno.length === 0) {
    return [];
  }

  return retorno.filter((item) => Boolean(item.critica?.trim() || item.descrCritica?.trim()));
};

export const isSeguradoInconsistencyError = (error: unknown): error is SeguradoInconsistencyError => (
  error instanceof SeguradoInconsistencyError
);

export const parseSeguradoInconsistenciesFromError = (error: unknown): SeguradoDataReturn[] => {
  if (isSeguradoInconsistencyError(error)) {
    return error.inconsistencies;
  }
  return [];
};

export const assertAddSeguradoDataResponse = (response: AddSeguradoDataResponse): void => {
  const inconsistencies = normalizeSeguradoInconsistencies(response.retorno);
  if (inconsistencies.length > 0) {
    throw new SeguradoInconsistencyError(inconsistencies);
  }

  if (isSelfBillingApiErrorMessage(response.mensagem)) {
    throw new Error(response.mensagem ?? 'Não foi possível incluir os dados do segurado');
  }
};

const hasSelfBillingResponseContent = (value: unknown): boolean => {
  if (value == null) {
    return false;
  }

  return String(value).trim() !== '';
};

export const assertUpdateSeguradoDataResponse = (response: UpdateSeguradoDataResponse): void => {
  const inconsistencies = normalizeSeguradoInconsistencies(response.retorno);
  if (inconsistencies.length > 0) {
    throw new SeguradoInconsistencyError(inconsistencies);
  }

  if (isSelfBillingApiErrorMessage(response.mensagem)) {
    throw new Error(response.mensagem ?? 'Não foi possível alterar os dados do segurado');
  }

  if (
    !hasSelfBillingResponseContent(response.mensagem)
    && !hasSelfBillingResponseContent(response.retorno)
  ) {
    throw new Error('Nenhuma alteração foi realizada nos dados do segurado.');
  }
};

const SEGURADO_SEX_LABELS: Record<string, string> = {
  M: 'Masculino',
  F: 'Feminino',
  '1': 'Masculino',
  '2': 'Feminino',
};

const SEGURADO_SEX_API_BY_LABEL: Record<string, string> = {
  Masculino: 'M',
  Feminino: 'F',
};

const TIPO_SEGURADO_LABELS: Record<string, string> = {
  P: 'Principal',
  C: 'Cônjuge',
};

const SEGURADO_STATUS_LABELS: Record<string, string> = {
  A: 'Alteração',
  I: 'Inclusão',
  N: 'Normal',
  R: 'Reativado',
  T: 'Retroativo',
};

export const SEGURADO_BILLING_STATUS = {
  ATIVO: 'Ativo',
  INATIVO: 'Inativo',
} as const;

export type SeguradoBillingStatus =
  (typeof SEGURADO_BILLING_STATUS)[keyof typeof SEGURADO_BILLING_STATUS];

export const isSeguradoBillingActive = (row: Pick<SelfBillingTableItem, 'status'>): boolean =>
  row.status === SEGURADO_BILLING_STATUS.ATIVO;

const TIPO_DOCUMENTO_LABELS: Record<number, string> = {
  1: 'R.G',
  2: 'CPF',
  3: 'CNPJ',
  4: 'Título Eleitoral',
  5: 'Habilitação',
  6: 'Inscrição Estadual',
  7: 'C.E.I',
  8: 'Outros',
  9: 'Certidão de Nascimento',
  10: 'CPF do Beneficiário',
  11: 'CNPJ do Beneficiário',
  12: 'RIC',
};

export const mapTipoSeguradoToLabel = (tipo: string): string => {
  const normalized = tipo.trim().toUpperCase();
  if (!normalized) {
    return '—';
  }
  return TIPO_SEGURADO_LABELS[normalized] ?? tipo.trim();
};

export const mapSeguradoStatusToLabel = (status: string): string => {
  const normalized = status.trim().toUpperCase();
  if (!normalized) {
    return '—';
  }
  return SEGURADO_STATUS_LABELS[normalized] ?? status.trim();
};

export const mapTipoDocumentoToLabel = (tipo: number | string | null | undefined): string => {
  if (tipo === null || tipo === undefined || tipo === '') {
    return '—';
  }

  const code = typeof tipo === 'number' ? tipo : Number(String(tipo).trim());
  if (!Number.isFinite(code) || code <= 0) {
    return '—';
  }

  return TIPO_DOCUMENTO_LABELS[code] ?? String(tipo);
};

export const formatSeguradoDocumento = (numDocumento: string): string => {
  const digits = numDocumento.replace(/\D/g, '');
  if (digits.length !== 11) {
    return numDocumento.trim() || '—';
  }
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
};

export const mapSeguradoSexToLabel = (sexo: string): string =>
  SEGURADO_SEX_LABELS[sexo.trim()] ?? sexo;

export const mapSeguradoSexToFormValue = (sexo: string): string => {
  const normalized = sexo.trim();
  if (!normalized) {
    return '';
  }
  if (normalized === '1' || normalized === '2') {
    return normalized;
  }
  return mapSeguradoSexToApi(normalized) === 'F' ? '2' : '1';
};

export const mapSeguradoSexToApi = (sexo: string, fallback = 'M'): string => {
  const normalized = sexo.trim();
  if (normalized === '1' || normalized === '2') {
    return normalized === '2' ? 'F' : 'M';
  }
  if (SEGURADO_SEX_API_BY_LABEL[normalized]) {
    return SEGURADO_SEX_API_BY_LABEL[normalized];
  }
  if (normalized === 'M' || normalized === 'F') {
    return normalized;
  }
  const code = mapSexLabelToCode(normalized, fallback === 'F' ? '2' : '1');
  return code === '2' ? 'F' : 'M';
};

const formatIsoDateForApi = (dataNascimento: string): string => {
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dataNascimento.trim());
  if (isoMatch) {
    return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
  }

  const brMatch = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(dataNascimento.trim());
  if (brMatch) {
    return `${brMatch[3]}-${brMatch[2]}-${brMatch[1]}`;
  }

  return dataNascimento.trim();
};

const formatSeguradoCompactDateForInput = (value: string): string =>
  formatDateForDateInput(value);

const formatMoneyForForm = (value: number): string => formatSalaryForFormInitial(value);

const formatSeguradoDateForApi = (value: string): string => {
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (isoMatch) {
    return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
  }

  const compact = /^(\d{4})(\d{2})(\d{2})$/.exec(value.trim());
  if (compact) {
    return `${compact[1]}-${compact[2]}-${compact[3]}`;
  }

  const timestampMatch = /^(\d{4}-\d{2}-\d{2})/.exec(value.trim());
  if (timestampMatch) {
    return timestampMatch[1];
  }

  return formatVigenciaDateForApi(value);
};

export const resolveNumVidaPreviaFat = (row: SelfBillingTableItem): number => {
  if (row.numVidaPreviaFat != null) {
    return row.numVidaPreviaFat;
  }

  throw new Error('Segurado não encontrado');
};

export const buildConsultarSeguradosFaturamentoParams = (
  row: SelfBillingTableItem,
): Pick<GetConsultarSeguradosFaturamentoParams, 'matricula' | 'nomeSegurado'> => {
  const matricula = row.matricula.trim();
  if (matricula && matricula !== '—') {
    return { matricula };
  }

  if (row.nome && row.nome !== '—') {
    return { nomeSegurado: row.nome };
  }

  return {};
};

export const findNumVidaPreviaFatFromConsulta = (
  row: SelfBillingTableItem,
  items: ConsultarSeguradosFaturamentoItem[] | null | undefined,
): number | null => {
  if (!items?.length) {
    return null;
  }

  const match = items.find((item) => item.identCertificado === row.identCertificado) ?? items[0];
  return match.numVidaPreviaFat ?? null;
};

const formatVigenciaDateForApi = (value: string): string => {
  const trimmed = value.trim();
  const slashMatch = /^(\d{4})\/(\d{2})\/(\d{2})$/.exec(trimmed);
  if (slashMatch) {
    return `${slashMatch[1]}-${slashMatch[2]}-${slashMatch[3]}`;
  }
  return formatIsoDateForApi(trimmed);
};

const buildSeguradoInclusionItem = (values: NewInsuredFormValues, dataInclusao: string) => ({
  cpf: values.cpf.replace(/\D/g, ''),
  matricula: values.matricula.trim(),
  nomeSegurado: values.nome.trim(),
  dataNascimento: formatIsoDateForApi(values.dataNascimento),
  sexoSegurado: mapSeguradoSexToApi(values.sexo),
  salario: formatSalaryDecimalForApi(values.salario),
  capital: parseFormMoneyToNumber(values.capital),
  dataInclusao: formatIsoDateForApi(dataInclusao),
  cep: getCepDigits(values.cep),
  logradouro: formatLogradouroWithNumeroForApi(values.logradouro, values.numero),
  bairro: values.bairro.trim(),
  cidade: values.cidade.trim(),
  uf: values.uf.trim(),
});

export const mapSeguradoListItemToTableRow = (
  item: SeguradoListItem,
): SelfBillingTableItem => ({
  id: String(item.identCertificado),
  matricula: item.codMatriculaSeg?.trim() || '—',
  tipoSegurado: mapTipoSeguradoToLabel(item.tipoSegurado?.trim() || 'P'),
  nome: item.nomeSegurado?.trim() || '—',
  cpf: formatSeguradoDocumento(item.numDocumento),
  tipoDocumento: mapTipoDocumentoToLabel(item.tipoDocumento),
  status: mapSeguradoStatusToLabel(item.codStatusSegFatura?.trim() || ''),
  certificado: item.numCertificado ? String(item.numCertificado) : '—',
  identCertificado: item.identCertificado,
  identSubestipulante: item.identSubestipulante,
  sexo: item.sexo,
  dataNasc: item.dataNasc,
  endereco: item.endereco,
  cidade: item.cidade,
  estado: item.estado,
  cep: item.cep,
  dataInicioVigencia: item.dataInicioVigencia,
  dataCompetencia: item.dataCompetencia,
  tipoSeguradoCodigo: item.tipoSegurado?.trim() || 'P',
  tipoDocumentoCodigo: item.tipoDocumento,
});

export const mapSeguradoTableRowToFormValues = (
  row: SelfBillingTableItem,
): NewInsuredFormValues => {
  const { logradouro, numero } = parseLogradouroFromApi(row.endereco?.trim() || '');

  return {
    nome: row.nome,
    matricula: row.matricula,
    cpf: row.cpf,
    sexo: mapSeguradoSexToFormValue(row.sexo),
    dataNascimento: formatSeguradoCompactDateForInput(row.dataNasc),
    dataCadastro: formatSeguradoCompactDateForInput(row.dataInicioVigencia),
    cep: formatCepForInput(row.cep),
    logradouro,
    numero,
    bairro: '',
    cidade: row.cidade?.trim() || '',
    uf: row.estado?.trim() || '',
    capital: '',
    salario: '',
  };
};

export const mapSeguradoDetailToFormValues = (
  detail: SeguradoDetailItem,
): NewInsuredFormValues => {
  const { logradouro, numero } = parseLogradouroFromApi(detail.endereco?.trim() || '');

  return {
    nome: detail.nome.trim(),
    matricula: detail.matricula.trim(),
    cpf: formatSeguradoDocumento(detail.cpf),
    sexo: mapSeguradoSexToFormValue(detail.sexo),
    dataNascimento: formatSeguradoCompactDateForInput(detail.dataNasc),
    dataCadastro: formatSeguradoCompactDateForInput(detail.dataInclusao || detail.dataVigencia),
    cep: formatCepForInput(detail.cep),
    logradouro,
    numero,
    bairro: detail.bairro?.trim() || '',
    cidade: detail.cidade?.trim() || '',
    uf: detail.estado?.trim() || '',
    capital: formatMoneyForForm(detail.capital),
    salario: formatSalaryForFormInitial(detail.salario),
  };
};

export const mapSeguradoDetailToTableRow = (
  detail: SeguradoDetailItem,
  currentRow: SelfBillingTableItem,
): SelfBillingTableItem => ({
  ...currentRow,
  id: String(detail.numVidaPreviaFat),
  numVidaPreviaFat: detail.numVidaPreviaFat,
  matricula: detail.matricula?.trim() || currentRow.matricula,
  tipoSegurado: mapTipoSeguradoToLabel(detail.tipoSegurado?.trim() || currentRow.tipoSeguradoCodigo),
  tipoSeguradoCodigo: detail.tipoSegurado?.trim() || currentRow.tipoSeguradoCodigo,
  nome: detail.nome?.trim() || currentRow.nome,
  cpf: formatSeguradoDocumento(detail.cpf),
  sexo: detail.sexo,
  dataNasc: detail.dataNasc,
  endereco: detail.endereco,
  cidade: detail.cidade,
  estado: detail.estado,
  cep: detail.cep,
  dataInicioVigencia: detail.dataVigencia || currentRow.dataInicioVigencia,
});

export const buildSeguradoListSearchParams = (
  search: string,
): Pick<GetSeguradoListParams, 'nomeSegurado' | 'numDocumento' | 'codMatriculaSeg'> => {
  const term = search.trim();
  if (term === '') {
    return {};
  }

  const digitsOnly = term.replace(/\D/g, '');
  if (digitsOnly.length === 11) {
    return { numDocumento: Number(digitsOnly) };
  }

  if (/^\d+$/.test(term)) {
    return { codMatriculaSeg: term };
  }

  return { nomeSegurado: term };
};

export type SeguradosPanelFilters = {
  matricula: string;
  tipoSegurado: string;
  nome: string;
  tipoDocumento: string;
  cpf: string;
  certificado: string;
};

export const EMPTY_SEGURADOS_PANEL_FILTERS: SeguradosPanelFilters = {
  matricula: '',
  tipoSegurado: '',
  nome: '',
  tipoDocumento: '',
  cpf: '',
  certificado: '',
};

const matchesSeguradoPanelTextFilter = (fieldValue: string, filterValue: string): boolean => {
  const term = filterValue.trim();
  if (term === '') {
    return true;
  }
  return fieldValue.toLowerCase().includes(term.toLowerCase());
};

export const filterSeguradoTableRows = (
  rows: SelfBillingTableItem[],
  filters: SeguradosPanelFilters | null,
): SelfBillingTableItem[] => {
  if (!filters) {
    return rows;
  }

  const hasAnyFilter = Object.values(filters).some((value) => value.trim() !== '');
  if (!hasAnyFilter) {
    return rows;
  }

  return rows.filter((row) => {
    const tipoSeguradoLabel = mapTipoSeguradoToLabel(row.tipoSeguradoCodigo || row.tipoSegurado);
    const tipoDocumentoLabel = mapTipoDocumentoToLabel(row.tipoDocumentoCodigo || row.tipoDocumento);

    return matchesSeguradoPanelTextFilter(row.matricula, filters.matricula)
      && (
        matchesSeguradoPanelTextFilter(tipoSeguradoLabel, filters.tipoSegurado)
        || matchesSeguradoPanelTextFilter(row.tipoSegurado, filters.tipoSegurado)
      )
      && matchesSeguradoPanelTextFilter(row.nome, filters.nome)
      && (
        matchesSeguradoPanelTextFilter(tipoDocumentoLabel, filters.tipoDocumento)
        || matchesSeguradoPanelTextFilter(String(row.tipoDocumento), filters.tipoDocumento)
      )
      && matchesSeguradoPanelTextFilter(row.cpf, filters.cpf)
      && matchesSeguradoPanelTextFilter(row.certificado, filters.certificado);
  });
};

export const sortSeguradoTableRows = (
  rows: SelfBillingTableItem[],
  sortBy: string,
): SelfBillingTableItem[] => {
  if (!sortBy) {
    return rows;
  }

  return [...rows].sort((a, b) => {
    switch (sortBy) {
      case 'matricula': return a.matricula.localeCompare(b.matricula);
      case 'matricula-desc': return b.matricula.localeCompare(a.matricula);
      case 'tipoSegurado': return a.tipoSegurado.localeCompare(b.tipoSegurado);
      case 'tipoSegurado-desc': return b.tipoSegurado.localeCompare(a.tipoSegurado);
      case 'nome': return a.nome.localeCompare(b.nome);
      case 'nome-desc': return b.nome.localeCompare(a.nome);
      case 'cpf': return a.cpf.localeCompare(b.cpf);
      case 'cpf-desc': return b.cpf.localeCompare(a.cpf);
      case 'status': return a.status.localeCompare(b.status);
      case 'status-desc': return b.status.localeCompare(a.status);
      case 'certificado': return a.certificado.localeCompare(b.certificado);
      case 'certificado-desc': return b.certificado.localeCompare(a.certificado);
      default: return 0;
    }
  });
};

const mapBeneficiarioToInclusionItem = (
  item: SeguradoDetailBeneficiaryItem,
): SeguradoBeneficiaryInclusionItem => ({
  nomeBeneficiario: item.nomeBeneficiario?.trim() ?? '',
  codTipoParentesco: item.codTipoParentesco != null && item.codTipoParentesco !== ''
    ? String(item.codTipoParentesco)
    : '',
  percentualPart: item.percentualPart ?? item.percentual ?? 0,
  codBneficiario: item.codBneficiario ?? null,
});

export const mapBeneficiariosToInclusionItems = (
  beneficiarios: SeguradoDetailBeneficiaryItem[] | null | undefined,
): SeguradoBeneficiaryInclusionItem[] => {
  if (!beneficiarios?.length) {
    return [];
  }

  return beneficiarios.map(mapBeneficiarioToInclusionItem);
};

export const buildAddSeguradoDataRequest = (params: {
  idSubestipulante: number;
  contrato: number;
  codigoUsuarioInclusao: string;
  formValues: NewInsuredFormValues;
  conjugueFormValues?: NewInsuredFormValues | null;
  beneficiarios?: SeguradoDetailBeneficiaryItem[] | null;
  dataInclusao?: string;
}): AddSeguradoDataRequest => {
  const dataInclusao = params.dataInclusao
    ?? params.formValues.dataCadastro
    ?? new Date().toISOString().slice(0, 10);
  const jsonBeneficiarios = mapBeneficiariosToInclusionItems(params.beneficiarios);

  return {
    idSubestipulante: params.idSubestipulante,
    contrato: params.contrato,
    usuario: params.codigoUsuarioInclusao,
    jsonSegurado: buildSeguradoInclusionItem(params.formValues, dataInclusao),
    ...(params.conjugueFormValues
      ? {
        jsonConjugue: buildSeguradoInclusionItem(
          params.conjugueFormValues,
          params.conjugueFormValues.dataCadastro ?? dataInclusao,
        ),
      }
      : {}),
    ...(jsonBeneficiarios.length > 0 ? { jsonBeneficiarios } : {}),
  };
};

export const buildAddConjugeSeguradoDataRequest = (params: {
  idSubestipulante: number;
  contrato: number;
  codigoUsuarioInclusao: string;
  formValues: NewInsuredFormValues;
  dataInclusao?: string;
}): AddConjugeSeguradoDataRequest => ({
  idSubestipulante: params.idSubestipulante,
  contrato: params.contrato,
  usuario: params.codigoUsuarioInclusao,
  jsonConjuge: buildSeguradoInclusionItem(
    params.formValues,
    params.dataInclusao
    ?? params.formValues.dataCadastro
    ?? new Date().toISOString().slice(0, 10),
  ),
});

export const assertAddConjugeSeguradoDataResponse = (
  response: AddConjugeSeguradoDataResponse,
): void => {
  const inconsistencies = normalizeSeguradoInconsistencies(response.retorno);
  if (inconsistencies.length > 0) {
    throw new SeguradoInconsistencyError(inconsistencies);
  }

  if (isSelfBillingApiErrorMessage(response.mensagem)) {
    throw new Error(response.mensagem ?? 'Não foi possível incluir os dados do cônjuge');
  }
};

export const buildUpdateSeguradoDataRequest = (params: {
  idSubestipulante: number;
  contrato: number;
  codigoUsuarioInclusao: string;
  formValues: NewInsuredFormValues;
  row: SelfBillingTableItem;
  beneficiarios?: SeguradoDetailBeneficiaryItem[];
}): UpdateSeguradoDataRequest => {
  const numVidaPreviaFat = resolveNumVidaPreviaFat(params.row);
  const dataInclusao = formatSeguradoDateForApi(
    params.row.dataInicioVigencia || new Date().toISOString().slice(0, 10),
  );
  const jsonTBnefcVidaPrvioFatmt = mapBeneficiariosToUpdateItems(
    params.beneficiarios,
    numVidaPreviaFat,
  );

  return {
    idSubestipulante: params.idSubestipulante,
    contrato: params.contrato,
    usuario: params.codigoUsuarioInclusao,
    jsonSegurado: {
      numVidaPreviaFat,
      ...buildSeguradoInclusionItem(params.formValues, dataInclusao),
      tipoSegurado: params.row.tipoSeguradoCodigo,
    },
    ...(jsonTBnefcVidaPrvioFatmt.length > 0 ? { jsonTBnefcVidaPrvioFatmt } : {}),
  };
};

export const buildRemoveSeguradoDataRequest = (params: {
  idSubestipulante: number;
  codigoUsuarioInclusao: string;
  rows: SelfBillingTableItem[];
}): RemoveSeguradoDataRequest => {
  const compFatmt = params.rows[0]?.dataCompetencia ?? 0;

  return {
    idSubestipulante: params.idSubestipulante,
    usuario: params.codigoUsuarioInclusao,
    compFatmt,
    vidasRemover: params.rows.map((row) => ({
      nVidaPrvioFatmt: String(resolveNumVidaPreviaFat(row)),
    })),
  };
};

const resolveBeneficiarioPercentual = (
  item: SeguradoDetailBeneficiaryItem,
): number => item.percentualPart ?? item.percentual ?? 0;

const resolveBeneficiarioParentescoLabel = (
  item: SeguradoDetailBeneficiaryItem,
): string => {
  const fromApi = item.parentesco?.trim();
  if (fromApi) {
    return fromApi;
  }
  return resolveSeguradoParentescoLabel(item.codTipoParentesco);
};

const resolveBeneficiarioCodTipoParentesco = (
  item: SeguradoDetailBeneficiaryItem,
): string => {
  if (item.codTipoParentesco == null || item.codTipoParentesco === '') {
    return '-';
  }
  return String(item.codTipoParentesco);
};

export const hasSeguradoBeneficiaries = (
  beneficiarios?: SeguradoDetailBeneficiaryItem[] | null,
): boolean => Boolean(beneficiarios?.length);

const mapBeneficiarioToUpdateItem = (
  item: SeguradoDetailBeneficiaryItem,
  defaultNVidaPrvioFatmt: number,
): SeguradoBeneficiaryUpdateItem => ({
  nVidaPrvioFatmt: item.nVidaPrvioFatmt ?? defaultNVidaPrvioFatmt,
  codBneficiario: item.codBneficiario ?? null,
  nomeBeneficiario: item.nomeBeneficiario?.trim() ?? '',
  codTipoParentesco: item.codTipoParentesco != null && item.codTipoParentesco !== ''
    ? String(item.codTipoParentesco)
    : '',
  percentualPart: item.percentualPart ?? item.percentual ?? 0,
});

export const mapBeneficiariosToUpdateItems = (
  beneficiarios: SeguradoDetailBeneficiaryItem[] | null | undefined,
  defaultNVidaPrvioFatmt: number,
): SeguradoBeneficiaryUpdateItem[] => {
  if (!beneficiarios?.length) {
    return [];
  }

  return beneficiarios.map((beneficiario) => (
    mapBeneficiarioToUpdateItem(beneficiario, defaultNVidaPrvioFatmt)
  ));
};

export const mapBeneficiarioToTableRow = (
  item: SeguradoDetailBeneficiaryItem,
  index: number,
): BeneficiarioTableItem => {
  const nomeBeneficiario = item.nomeBeneficiario?.trim() || '-';
  const codTipoParentesco = resolveBeneficiarioCodTipoParentesco(item);

  return {
    id: `${item.codBneficiario ?? 'beneficiario'}-${codTipoParentesco}-${index}`,
    nomeBeneficiario,
    codTipoParentesco,
    parentesco: resolveBeneficiarioParentescoLabel(item),
    percentual: resolveBeneficiarioPercentual(item),
    codBneficiario: item.codBneficiario ?? null,
    nVidaPrvioFatmt: item.nVidaPrvioFatmt,
    source: item,
  };
};

export const mapBeneficiariosToTableRows = (
  beneficiarios: SeguradoDetailBeneficiaryItem[],
): BeneficiarioTableItem[] => beneficiarios.map((beneficiario, index) => (
  mapBeneficiarioToTableRow(beneficiario, index)
));

export const formatBeneficiarioPercentual = (percentual: number): string => (
  Number.isFinite(percentual) ? `${percentual}%` : '-'
);

export { resolveCodigoUsuarioInclusao };
